"use client";

import React from "react";
import { cn } from "@repo/utils/cn";
import { Button, ButtonProps } from "./Button";
import { HelpIcon } from "./icons/HelpIcon";
import { Tooltip } from "./Tooltip";

export interface CalculatorResult {
  label: string;
  value: number;
  tooltip?: string;
  _uid?: string;
  _editable?: string;
}

export interface CalculatorResultsProps {
  totalResult: number;
  totalLabel: string;
  totalUnits: string;
  results: CalculatorResult[];
  description?: string;
  button?: ButtonProps;
  className?: string;
}

export const CalculatorResults = React.forwardRef<HTMLDivElement, CalculatorResultsProps>(
  ({
    totalResult,
    totalLabel,
    totalUnits,
    results,
    description,
    button,
    className
  }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "uipm:bg-[#f4f4f4] uipm:rounded-[20px] uipm:p-6 uipm:flex uipm:flex-col uipm:gap-12 uipm:min-w-[280px]",
          className
        )}
      >
        <div className="uipm:flex uipm:gap-4 uipm:h-[86px] uipm:items-center">
          <div className="uipm:font-['Inter'] uipm:font-normal uipm:text-[76px] uipm:leading-[1.1] uipm:tracking-[-1.52px] uipm:text-[#3ebd37]">
            {totalResult}
          </div>
          <div className="uipm:flex-1 uipm:font-['Inter'] uipm:font-normal uipm:text-[22px] uipm:leading-[24px] uipm:text-[#38724e]">
            <p className="uipm:mb-0">{totalLabel}</p>
            <p>{totalUnits}</p>
          </div>
        </div>

        <div className="uipm:flex uipm:flex-col uipm:gap-4">
          <div className="uipm:flex uipm:flex-wrap uipm:gap-6">
            {results.map((result, index) => (
              <div 
                key={result._uid || index}
                className="uipm:flex-1 uipm:min-w-[160px] uipm:flex uipm:flex-col"
              >
                <div className="uipm:flex uipm:gap-1 uipm:items-center">
                  <div className="uipm:font-['Inter'] uipm:font-bold uipm:text-[22px] uipm:leading-[26px] uipm:text-[#38724e]">
                    {result.value} days
                  </div>
                  {result.tooltip && (
                    <Tooltip 
                      content={result.tooltip}
                      className="uipm:w-6 uipm:h-6 uipm:flex uipm:items-center uipm:justify-center uipm:cursor-help"
                    >
                      <HelpIcon className="uipm:w-5 uipm:h-5" />
                    </Tooltip>
                  )}
                </div>
                <div className="uipm:font-['Inter'] uipm:font-medium uipm:text-[16px] uipm:leading-[26px] uipm:text-[#575757]">
                  {result.label}
                </div>
              </div>
            ))}
          </div>
          
          {description && (
            <div className="uipm:font-['Inter'] uipm:font-medium uipm:text-[16px] uipm:leading-[26px] uipm:text-[#828282]">
              {description}
            </div>
          )}
        </div>

        {button?.text && (
          <Button
            {...button}
            className="uipm:bg-[#38724e] uipm:text-white uipm:rounded-[12px] uipm:h-[58px] uipm:min-w-[120px] uipm:font-['Inter'] uipm:font-semibold uipm:text-[16px] uipm:leading-[26px]"
          >
            {button.text}
          </Button>
        )}
      </div>
    );
  }
);

CalculatorResults.displayName = "CalculatorResults";